/**
 * ColorPickerField — a modern color control for the report editor.
 *
 * Replaces the native <input type="color"> (dated, OS-themed) with a swatch button
 * that opens a popover containing react-colorful's HexColorPicker + a hex text
 * input + an optional Reset. Matches the panel's design tokens (rounded-lg,
 * border-[0.5px] border-input, text-[13.5px]).
 */

import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Popover, PopoverTrigger, PopoverContent } from "@provenleap/ui";

export interface ColorPickerFieldProps {
  /** Current colour (hex). When undefined the swatch shows `defaultValue`. */
  value?: string;
  /** Commit a new colour. */
  onChange: (hex: string) => void;
  /** Swatch/picker colour when `value` is unset (the inherited default). */
  defaultValue?: string;
  /** When provided, a Reset action clears the override (passes undefined-equivalent). */
  onReset?: () => void;
  ariaLabel: string;
  testId?: string;
}

/** Normalize loose hex input ("fff", "#FFF", "ffffff") to "#rrggbb" or null. */
function normalizeHex(input: string): string | null {
  let h = input.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split("").map((c) => c + c).join("");
  if (/^[0-9a-fA-F]{6}$/.test(h)) return `#${h.toLowerCase()}`;
  return null;
}

export function ColorPickerField({ value, onChange, defaultValue = "#1e2a44", onReset, ariaLabel, testId }: ColorPickerFieldProps) {
  const current = value ?? defaultValue;
  // Local text state so a user can type a partial hex without it being rejected mid-edit.
  const [hexDraft, setHexDraft] = useState<string>(current);

  // Only propagate a genuinely-different colour. react-colorful re-emits onChange
  // whenever its `color` prop is fed back (even if equivalent, e.g. case/format
  // differences) — without this guard the parent → re-render → onChange cycle
  // loops forever ("Maximum update depth exceeded").
  const commit = (hex: string) => {
    if (hex.toLowerCase() !== current.toLowerCase()) onChange(hex);
  };

  return (
    // The swatch fills the control column (same h-9 + width as the other fields).
    // `flex` (not the default inline-block) removes the baseline gap that made the
    // swatch sit slightly low vs its label. Reset lives inside the popover.
    <div className="flex w-full">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            data-testid={testId}
            onClick={() => setHexDraft(current)}
            className="touch-target-sm block h-9 w-full cursor-pointer rounded-lg border-[0.5px] border-input shadow-xs"
            style={{ backgroundColor: current }}
          />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto rounded-xl border-[0.5px] border-input p-3 shadow-md">
          <div className="space-y-2.5" data-testid={testId ? `${testId}-popover` : undefined}>
            <HexColorPicker
              color={current}
              onChange={(hex) => { setHexDraft(hex); commit(hex); }}
            />
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] text-muted-foreground">#</span>
              <input
                type="text"
                aria-label={`${ariaLabel} hex value`}
                value={hexDraft.replace(/^#/, "")}
                onChange={(e) => {
                  setHexDraft(e.target.value);
                  const norm = normalizeHex(e.target.value);
                  if (norm) commit(norm);
                }}
                className="h-8 w-full rounded-lg border-[0.5px] border-input bg-background px-2 text-[13.5px] uppercase shadow-xs focus:outline-none focus-visible:outline-none"
                maxLength={7}
              />
            </div>
            {/* Reset to the inherited default — inside the popover so it never
                shifts the row layout. Only when a custom colour is set. */}
            {onReset && value && (
              <button
                type="button"
                aria-label={`Clear ${ariaLabel}`}
                onClick={onReset}
                className="w-full rounded-lg border-[0.5px] border-input py-1.5 text-[13px] text-muted-foreground hover:bg-accent/50 hover:text-foreground cursor-pointer"
              >
                Reset to default
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
